import type { WorldSlug } from '@/lib/worlds';

// SI-Inspiration content lock 2026-05-11 (Sebastian Gate-1).
// Replaces the prior 6 emoji-tile entries with 19 prose-card
// entries — driven by the new IdeaCard component which renders a
// body subtitle slot alongside the emoji + title (Variant D).
// Tap navigates to /builder/{world} (no theme prefill — Decision
// 3B, SI-Inspiration).
//
// Anker-Items (aa-anker-sportstar, aa-anker-lieblingsheldin)
// intentionally redundant with the Sportstar + Lieblings-Heldin
// tips in TIPS — different surfaces, different framing, same
// retention design per Sebastian's lock.
//
// Event entries (event-besuch-story, event-familie-story) use
// world='aa' as a default fallback because IdeaCardData requires
// a concrete WorldSlug — these stories are scenario-led rather
// than world-led, so AA (the all-purpose everyday world) is the
// sensible carrier.

export interface IdeaCardData {
  /** Stable slug for React keys + tests. */
  id: string;
  /** Lead emoji rendered inside the card. */
  emoji: string;
  /** Card title — short noun-phrase or question. */
  name: string;
  /** Body subtitle — 1-2 sentences setting the scene. */
  subtitle: string;
  /** Target world slug — drives the world-label chip and navigation target. */
  world: WorldSlug;
}

export const STORY_IDEAS: readonly IdeaCardData[] = [
  // ── Abenteuer Alltag — 5 everyday-life cards ─────────────────
  {
    id: 'aa-feuerwehr',
    emoji: '🚒',
    name: 'Besuch bei der Feuerwehr',
    subtitle:
      'Eine Führung im Feuerwehrhaus, ein Probealarm und am Ende vielleicht ein kurzer Ritt auf dem Tankwagen.',
    world: 'aa',
  },
  {
    id: 'aa-eis-papa',
    emoji: '🍦',
    name: 'Eis essen mit Papa',
    subtitle:
      'Ein spontaner Nachmittag, eine schwierige Sortenwahl, und das Eis tropft genau im falschen Moment.',
    world: 'aa',
  },
  {
    id: 'aa-streit-spielplatz',
    emoji: '🤝',
    name: 'Streit auf dem Spielplatz',
    subtitle:
      'Zwei wollen die gleiche Schaukel — und am Ende finden sie eine Lösung, auf die niemand vorher gekommen wäre.',
    world: 'aa',
  },
  {
    id: 'aa-arztbesuch',
    emoji: '🩺',
    name: 'Der erste Arztbesuch',
    subtitle:
      'Aufgeregt zur Praxis, ein bisschen Bauchweh, dann ein guter Doktor und ein Sticker als Belohnung.',
    world: 'aa',
  },
  {
    id: 'aa-schwimmen',
    emoji: '🏊',
    name: 'Schwimmen lernen im Schwimmbad',
    subtitle:
      'Erst nur am Rand, dann mit Schwimmflügeln, dann ein erster echter Zug — und plötzlich klappt es.',
    world: 'aa',
  },
  // ── Abenteuer Alltag — 2 Anker (intentional redundancy with TIPS) ──
  {
    id: 'aa-anker-sportstar',
    emoji: '⚽',
    name: 'Ein Tag mit dem Lieblings-Sportstar',
    subtitle:
      'Das Lieblings-Idol kommt zum Trainieren in den Park, in den Garten oder ins Stadion — und dein Kind ist mit dabei.',
    world: 'aa',
  },
  {
    id: 'aa-anker-lieblingsheldin',
    emoji: '👫',
    name: 'Mit der Lieblings-Heldin auf Abenteuer',
    subtitle:
      'Die Figur aus dem Lieblings-Buch oder der Lieblings-Serie kommt vorbei und braucht heute Verstärkung — und nur dein Kind kann helfen.',
    world: 'aa',
  },
  // ── Schlauberger — 5 curious-question cards ──────────────────
  {
    id: 'sb-schmetterling',
    emoji: '🦋',
    name: 'Wie wird aus einer Raupe ein Schmetterling?',
    subtitle:
      'Eine Forscher-Reise durch die Verwandlung — mit echten Stadien, echten Farben, einer Frage an dein Kind am Ende.',
    world: 'sb',
  },
  {
    id: 'sb-vulkan',
    emoji: '🌋',
    name: 'Warum spucken Vulkane Feuer?',
    subtitle:
      'Tief in die Erde rein, schauen wie heiß es da unten ist und verstehen, wie ein Vulkan eigentlich tickt.',
    world: 'sb',
  },
  {
    id: 'sb-giraffe',
    emoji: '🦒',
    name: 'Warum hat die Giraffe so einen langen Hals?',
    subtitle:
      'Eine Safari mit echten Fakten — wie hoch reicht so ein Hals, was frisst sie damit, warum genau so lang?',
    world: 'sb',
  },
  {
    id: 'sb-biene',
    emoji: '🐝',
    name: 'Wie macht eine Biene Honig?',
    subtitle:
      'Ein Tag im Bienenstock, vom Blüten-Anflug bis zur fertigen Wabe — mit echten Schritten und einem Geschmack am Ende.',
    world: 'sb',
  },
  {
    id: 'sb-sterne',
    emoji: '🌌',
    name: 'Warum funkeln die Sterne am Himmel?',
    subtitle:
      'Eine Reise hinaus ins All und zurück — was sind Sterne eigentlich, warum blinken sie, wie weit weg sind sie?',
    world: 'sb',
  },
  // ── Kuschelpatrouille — 5 calm bedtime cards ─────────────────
  {
    id: 'kp-aufgeregt',
    emoji: '😴',
    name: 'Noch viel zu aufgeregt zum Einschlafen',
    subtitle:
      'Heute war so viel los, der Kopf will einfach nicht zur Ruhe — eine ruhige Geschichte hilft beim Runterkommen.',
    world: 'kp',
  },
  {
    id: 'kp-geraeusch',
    emoji: '🌬️',
    name: 'Ein leises Geräusch im Zimmer',
    subtitle:
      'Da knackt was im Schrank, da raschelt was am Fenster — und am Ende ist es nur der Vorhang im Wind.',
    world: 'kp',
  },
  {
    id: 'kp-kopf-voll',
    emoji: '🌅',
    name: 'Schöner Tag, Kopf noch voll',
    subtitle:
      'So viele tolle Sachen erlebt, jetzt fällt das Loslassen schwer — eine Geschichte zum Sortieren der Gedanken.',
    world: 'kp',
  },
  {
    id: 'kp-zahnarzt',
    emoji: '🦷',
    name: 'Mut-Moment vor dem Zahnarztbesuch',
    subtitle:
      'Morgen ist Premiere beim Zahnarzt, heute hilft eine kleine Mut-Geschichte beim Einschlafen.',
    world: 'kp',
  },
  {
    id: 'kp-heimweh',
    emoji: '💛',
    name: 'Heimweh nach Oma beim Einschlafen',
    subtitle:
      'Oma ist weit weg, aber in den Träumen kann man sich besuchen — eine ruhige Geschichte zum Loslassen.',
    world: 'kp',
  },
  // ── Event-led — 2 scenario cards (world='aa' default) ────────
  {
    id: 'event-besuch-story',
    emoji: '🎉',
    name: 'Geschichte, wenn Besuch da ist',
    subtitle:
      'Alle Anwesenden bekommen eine Rolle — und gemeinsam wird geraten, was als nächstes passiert.',
    world: 'aa',
  },
  {
    id: 'event-familie-story',
    emoji: '🎊',
    name: 'Familien-Geschichten-Abend',
    subtitle:
      'Heute bestimmt ein anderes Familien-Mitglied das Thema — und alle, die zuhören, tauchen in der Geschichte auf.',
    world: 'aa',
  },
] as const;
